import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
    ArrowLeft,
    Info,
    KanbanSquare,
    MapPin,
    MapPinOff,
    RefreshCw,
    Search,
    Snowflake,
    X,
    Zap
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { MapaLead, Profile } from '../../../types';
import NegotiationDetailModal from '../components/NegotiationDetails/NegotiationDetailModal';
import ResponsibleFilter from '../../../src/components/ResponsibleFilter';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import {
    getLeadMapColor,
    getLeadMapLabel,
    leadMatchesStatusFilter,
    MAP_LEGEND,
    MAP_STATUS_FILTERS
} from '../../../src/lib/crmVisuals';

// ---------- Pins ----------
// Pin de localização (gota) padronizado — mesmo formato para todos os leads, cor por etapa do funil.
const iconCache: Record<string, L.DivIcon> = {};
const getPinIcon = (color: string, congelado: boolean) => {
    const key = `${color}-${congelado ? 'c' : 'n'}`;
    if (!iconCache[key]) {
        iconCache[key] = L.divIcon({
            className: 'vigorgeo-pin',
            html: `<div style="position:relative;width:26px;height:36px;">
                <svg width="26" height="36" viewBox="0 0 26 36" xmlns="http://www.w3.org/2000/svg" style="display:block;filter:drop-shadow(0 2px 3px rgba(0,0,0,.35));">
                    <path d="M13 0C5.82 0 0 5.82 0 13c0 9.2 11.05 21.3 12.04 22.36a1.3 1.3 0 0 0 1.92 0C14.95 34.3 26 22.2 26 13 26 5.82 20.18 0 13 0z" fill="${color}" stroke="#ffffff" stroke-width="2"/>
                    <circle cx="13" cy="13" r="5" fill="#ffffff"/>
                </svg>
                ${congelado ? `<span style="position:absolute;top:6px;left:0;width:26px;text-align:center;font-size:10px;line-height:14px;color:${color};">❄</span>` : ''}
            </div>`,
            iconSize: [26, 36],
            iconAnchor: [13, 36],
            popupAnchor: [0, -34],
        });
    }
    return iconCache[key];
};

// Cluster: bolha única com a contagem; tamanho escala com a concentração (maior = mais relevante).
const createClusterCustomIcon = (cluster: any) => {
    const count = cluster.getChildCount();
    const size = count >= 100 ? 58 : count >= 50 ? 50 : count >= 10 ? 44 : 38;
    const fontSize = count >= 100 ? 16 : count >= 50 ? 15 : 13;
    return L.divIcon({
        html: `<div class="bg-primary" style="width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:${fontSize}px;border-radius:9999px;border:3px solid rgba(255,255,255,.92);box-shadow:0 2px 8px rgba(0,0,0,.35);">${count}</div>`,
        className: 'vigorgeo-cluster',
        iconSize: L.point(size, size, true),
    });
};

/** Voa até o lead selecionado na sidebar */
const FlyToLead: React.FC<{ target: [number, number] | null }> = ({ target }) => {
    const map = useMap();
    useEffect(() => {
        if (target) map.flyTo(target, Math.max(map.getZoom(), 13), { duration: 0.8 });
    }, [target?.[0], target?.[1]]);
    return null;
};

const formatKwh = (v: number) => `${Math.round(v || 0).toLocaleString('pt-BR')} kWh`;

const diasColor = (dias: number, congelado: boolean) => {
    if (congelado) return 'text-red-500';
    if (dias > 7) return 'text-yellow-500';
    return 'text-green-500';
};

const MapaLeadsView: React.FC = () => {
    const { profile } = useAuth();
    const { addToast } = useToast();
    const navigate = useNavigate();

    const [leads, setLeads] = useState<MapaLead[]>([]);
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(true);
    const [geocoding, setGeocoding] = useState(false);

    // Filtros
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('todos');
    const [segmentoFilter, setSegmentoFilter] = useState('todos');
    const [selectedResponsibles, setSelectedResponsibles] = useState<string[]>([]);

    // UI
    const [showLegend, setShowLegend] = useState(true);
    const [showSidebarMobile, setShowSidebarMobile] = useState(false);
    const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);
    const [detailId, setDetailId] = useState<number | null>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const isManager = profile?.role === 'adm' || profile?.role === 'gerente';

    const fetchLeads = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('vw_mapa_leads')
                .select('*')
                .order('ultima_atividade_at', { ascending: false });

            // Mesma regra de visibilidade do Kanban
            if (profile?.role === 'vendedor' || profile?.role === 'vendedor_parceiro') {
                query = query.eq('responsavel_id', profile.id);
            } else if (profile?.role === 'parceiro') {
                const { data: managed } = await supabase.from('profiles').select('id').eq('parceiro_id', profile.id);
                const managedIds = managed?.map(m => m.id) || [];
                query = query.in('responsavel_id', [profile.id, ...managedIds]);
            } else if (profile?.role === 'gerente_parcerias') {
                const { data: parceiroUsers } = await supabase.from('profiles').select('id').in('role', ['parceiro', 'vendedor_parceiro']);
                const parceiroIds = parceiroUsers?.map(m => m.id) || [];
                query = query.in('responsavel_id', [profile.id, ...parceiroIds]);
            }

            const { data, error } = await query;
            if (error) throw error;
            setLeads((data || []) as MapaLead[]);
        } catch (err) {
            console.error('Erro ao buscar leads do mapa:', err);
            addToast('Erro ao carregar os leads do mapa.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchProfiles = async () => {
        // Conta de consumidor (Portal) nunca entra em lista de escolha do sistema.
        const { data } = await supabase.from('profiles').select('*').neq('role', 'consumidor');
        if (data) setProfiles(data);
    };

    useEffect(() => {
        if (profile) {
            fetchLeads();
            fetchProfiles();
        }
    }, [profile?.id]);

    // ---------- Filtragem ----------
    const segmentosDisponiveis = useMemo(() => {
        const set = new Set<string>();
        leads.forEach(l => { if (l.segmento?.trim()) set.add(l.segmento.trim()); });
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [leads]);

    const filteredLeads = useMemo(() => {
        const term = search.trim().toLowerCase();
        return leads.filter(l => {
            if (!leadMatchesStatusFilter(l, statusFilter)) return false;
            if (segmentoFilter !== 'todos' && (l.segmento?.trim() || '') !== segmentoFilter) return false;
            if (selectedResponsibles.length > 0 && !selectedResponsibles.includes(String(l.responsavel_id || ''))) return false;
            if (term) {
                const haystack = `${l.titulo} ${l.cliente_nome} ${l.cidade || ''} ${l.bairro || ''} ${l.segmento || ''}`.toLowerCase();
                if (!haystack.includes(term)) return false;
            }
            return true;
        });
    }, [leads, search, statusFilter, segmentoFilter, selectedResponsibles]);

    const counters = useMemo(() => {
        const aprovados = filteredLeads.filter(l => ['termo_aprovado', 'termo_assinado', 'ganho'].includes(l.status)).length;
        const congelados = filteredLeads.filter(l => l.congelado).length;
        const ativos = filteredLeads.length - aprovados - congelados;
        const semLocalizacao = filteredLeads.filter(l => !l.latitude || !l.longitude).length;
        return { ativos: Math.max(0, ativos), congelados, aprovados, semLocalizacao };
    }, [filteredLeads]);

    const mappableLeads = useMemo(
        () => filteredLeads.filter(l => l.latitude && l.longitude),
        [filteredLeads]
    );

    const pendingGeocode = useMemo(
        () => leads.filter(l => l.geocode_status === 'pendente' && l.cidade).length,
        [leads]
    );

    // ---------- Ações ----------
    const handleGeocode = async () => {
        setGeocoding(true);
        try {
            const { data, error } = await supabase.functions.invoke('geocode-leads', { body: { batch: 20 } });
            if (error) throw error;
            const ok = (data?.processed || []).filter((p: any) => p.status !== 'error' && p.status !== 'falhou').length;
            addToast(`Geocodificação: ${ok} leads localizados. Restam ${data?.restantes ?? '?'} pendentes.`, 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'mapa', acao: 'geocodificar_leads', origem: 'acao' });
            await fetchLeads();
        } catch (err) {
            console.error('Erro ao geocodificar:', err);
            addToast('Erro ao geocodificar leads.', 'error');
        } finally {
            setGeocoding(false);
        }
    };

    const handleSelectLead = (lead: MapaLead) => {
        if (lead.latitude && lead.longitude) {
            setFlyTarget([lead.latitude, lead.longitude]);
            setShowSidebarMobile(false);
        } else {
            setDetailId(lead.id);
        }
    };

    const responsavelNome = (id?: string | null) => {
        if (!id) return 'Sem responsável';
        const p = profiles.find(pr => String(pr.id) === String(id));
        return p?.full_name?.split(' ')[0] || '—';
    };

    // ---------- Render ----------
    return (
        <div className="flex flex-col gap-3 px-2 sm:px-0">
            {/* Header do módulo */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/crm')}
                        className="p-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-primary transition-colors"
                        title="Voltar ao Kanban"
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <div className="p-2.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl">
                        <MapPin size={22} />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Mapa Inteligente</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Pipeline comercial georreferenciado</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    {counters.congelados > 0 && (
                        <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                            <Snowflake size={13} /> {counters.congelados} congelados
                        </span>
                    )}
                    {counters.semLocalizacao > 0 && (
                        <span
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                            title="Leads sem coordenadas (sem cidade/endereço cadastrado)"
                        >
                            <MapPinOff size={13} /> {counters.semLocalizacao} sem localização
                        </span>
                    )}
                    {isManager && pendingGeocode > 0 && (
                        <button
                            onClick={handleGeocode}
                            disabled={geocoding}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-semibold rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors disabled:opacity-50"
                        >
                            <RefreshCw size={13} className={geocoding ? 'animate-spin' : ''} />
                            {geocoding ? 'Geocodificando...' : `Geolocalizar pendentes (${pendingGeocode})`}
                        </button>
                    )}
                    <button
                        onClick={() => setShowSidebarMobile(!showSidebarMobile)}
                        className="md:hidden flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full bg-primary text-white"
                    >
                        <KanbanSquare size={14} /> Leads ({filteredLeads.length})
                    </button>
                </div>
            </div>

            <div className="relative flex gap-3 h-[calc(100vh-15rem)] min-h-[480px]">
                {/* Sidebar Pipeline de Leads */}
                <aside
                    className={`${showSidebarMobile ? 'absolute inset-0 z-[1001]' : 'hidden'} md:relative md:z-20 md:flex md:w-[340px] shrink-0 flex-col bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm`}
                >
                    <div className="p-4 pb-3 border-b border-gray-100 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">Pipeline de Leads</h2>
                            <button onClick={() => setShowSidebarMobile(false)} className="md:hidden p-1 text-gray-400">
                                <X size={16} />
                            </button>
                        </div>

                        {/* Contadores */}
                        <div className="grid grid-cols-3 gap-2 mb-3">
                            <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 px-2 py-2 text-center">
                                <p className="text-lg font-bold text-blue-600 dark:text-blue-400 leading-none">{counters.ativos}</p>
                                <p className="text-[10px] font-semibold text-blue-600/70 dark:text-blue-400/70 mt-1">Ativos</p>
                            </div>
                            <div className="rounded-xl bg-orange-50 dark:bg-orange-900/20 px-2 py-2 text-center">
                                <p className="text-lg font-bold text-orange-600 dark:text-orange-400 leading-none">{counters.congelados}</p>
                                <p className="text-[10px] font-semibold text-orange-600/70 dark:text-orange-400/70 mt-1">Congelados</p>
                            </div>
                            <div className="rounded-xl bg-green-50 dark:bg-green-900/20 px-2 py-2 text-center">
                                <p className="text-lg font-bold text-green-600 dark:text-green-400 leading-none">{counters.aprovados}</p>
                                <p className="text-[10px] font-semibold text-green-600/70 dark:text-green-400/70 mt-1">Aprovados</p>
                            </div>
                        </div>

                        {/* Busca */}
                        <div className="relative mb-2">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Buscar lead, cidade ou bairro..."
                                className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                        </div>

                        {/* Filtros */}
                        <div className="flex items-center gap-2 mb-2">
                            <select
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                                className="flex-1 min-w-0 text-xs font-medium px-2 py-2 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none"
                            >
                                {MAP_STATUS_FILTERS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                            {profile?.role !== 'vendedor' && profile?.role !== 'vendedor_parceiro' && (
                                <ResponsibleFilter
                                    selectedResponsibles={selectedResponsibles}
                                    setSelectedResponsibles={setSelectedResponsibles}
                                    profiles={profiles}
                                    currentUserProfile={profile}
                                    viewContext="crm"
                                />
                            )}
                        </div>
                        {segmentosDisponiveis.length > 0 && (
                            <select
                                value={segmentoFilter}
                                onChange={e => setSegmentoFilter(e.target.value)}
                                className="w-full text-xs font-medium px-2 py-2 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none"
                            >
                                <option value="todos">Todos os Segmentos</option>
                                {segmentosDisponiveis.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Lista de leads */}
                    <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
                        {loading ? (
                            <div className="flex justify-center py-10"><LoadingSpinner label="Carregando leads..." size="sm" /></div>
                        ) : filteredLeads.length === 0 ? (
                            <p className="text-center text-sm text-gray-400 py-10">Nenhum lead encontrado.</p>
                        ) : (
                            filteredLeads.map(lead => {
                                const color = getLeadMapColor(lead);
                                return (
                                    <button
                                        key={lead.id}
                                        onClick={() => handleSelectLead(lead)}
                                        onDoubleClick={() => setDetailId(lead.id)}
                                        className="w-full text-left rounded-xl p-3 transition-all duration-150 bg-gray-50/60 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700/60 border border-transparent hover:border-gray-200 dark:hover:border-gray-600 group"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{lead.titulo}</p>
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                                    {[lead.bairro, lead.cidade].filter(Boolean).join(' · ') || 'Sem localização'}
                                                    {lead.segmento ? ` · ${lead.segmento}` : ''}
                                                </p>
                                            </div>
                                            <div className="flex flex-col items-end gap-1 shrink-0">
                                                <span className={`text-[10px] font-bold ${diasColor(lead.dias_sem_atividade, lead.congelado)}`}>
                                                    {lead.dias_sem_atividade}d
                                                </span>
                                                {!lead.latitude && <MapPinOff size={11} className="text-gray-400" />}
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between mt-1.5">
                                            <span
                                                className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                                                style={{ backgroundColor: color }}
                                            >
                                                {lead.congelado ? '❄ Congelado' : getLeadMapLabel(lead)}
                                            </span>
                                            <span className="flex items-center gap-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                                                <Zap size={11} className="text-amber-500" /> {formatKwh(lead.consumo_medio_kwh)}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 text-[11px] text-gray-400 flex items-center justify-between">
                        <span>{filteredLeads.length} leads · {mappableLeads.length} no mapa</span>
                        <span className="hidden md:inline">2 cliques = detalhes</span>
                    </div>
                </aside>

                {/* Mapa */}
                <div className="flex-1 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-inner relative z-0">
                    {loading ? (
                        <div className="w-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    ) : (
                        <MapContainer
                            center={[-16.68, -49.25]} // Goiânia
                            zoom={7}
                            scrollWheelZoom={true}
                            className="h-full w-full"
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <FlyToLead target={flyTarget} />
                            <MarkerClusterGroup
                                chunkedLoading
                                iconCreateFunction={createClusterCustomIcon}
                                showCoverageOnHover={false}
                                spiderfyOnMaxZoom
                                zoomToBoundsOnClick
                                animate
                                maxClusterRadius={60}
                            >
                                {mappableLeads.map(lead => (
                                    <Marker
                                        key={lead.id}
                                        position={[lead.latitude!, lead.longitude!]}
                                        icon={getPinIcon(getLeadMapColor(lead), lead.congelado)}
                                    >
                                        <Popup>
                                            <div className="min-w-[190px]">
                                                <p className="font-semibold text-gray-900 border-b pb-1 mb-1.5">{lead.titulo}</p>
                                                <p className="text-xs text-gray-600 mb-0.5">
                                                    {[lead.bairro, lead.cidade].filter(Boolean).join(' · ')}
                                                    {lead.segmento ? ` · ${lead.segmento}` : ''}
                                                </p>
                                                <p className="text-xs text-gray-600 mb-0.5">
                                                    <span className="font-medium">Etapa:</span> {lead.congelado ? '❄ Congelado' : getLeadMapLabel(lead)}
                                                </p>
                                                <p className="text-xs text-gray-600 mb-0.5">
                                                    <span className="font-medium">Consumo:</span> {formatKwh(lead.consumo_medio_kwh)}
                                                </p>
                                                <p className="text-xs text-gray-600 mb-2">
                                                    <span className="font-medium">Responsável:</span> {responsavelNome(lead.responsavel_id)} · {lead.dias_sem_atividade}d sem atividade
                                                </p>
                                                {lead.geocode_status === 'cidade' && (
                                                    <p className="text-[10px] text-gray-400 mb-2 flex items-center gap-1">
                                                        <Info size={10} /> Localização aproximada (cidade)
                                                    </p>
                                                )}
                                                <button
                                                    onClick={() => setDetailId(lead.id)}
                                                    className="w-full text-center text-xs font-bold text-white bg-primary rounded-lg py-1.5 hover:opacity-90 transition-opacity"
                                                >
                                                    Ver detalhes
                                                </button>
                                            </div>
                                        </Popup>
                                    </Marker>
                                ))}
                            </MarkerClusterGroup>
                        </MapContainer>
                    )}

                    {/* Legenda */}
                    <div className="absolute top-3 right-3 z-[1000]">
                        {showLegend ? (
                            <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg p-3 w-48">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[11px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wide">Status dos Pins</p>
                                    <button onClick={() => setShowLegend(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                        <X size={13} />
                                    </button>
                                </div>
                                <div className="space-y-1">
                                    {MAP_LEGEND.map(item => (
                                        <div key={item.label} className="flex items-center gap-2">
                                            <span className="w-2.5 h-2.5 rounded-full border border-white shadow" style={{ backgroundColor: item.color }}></span>
                                            <span className="text-[11px] text-gray-600 dark:text-gray-300">{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowLegend(true)}
                                className="bg-white/95 dark:bg-gray-800/95 backdrop-blur rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg p-2.5 text-gray-500 hover:text-primary transition-colors"
                                title="Mostrar legenda"
                            >
                                <Info size={16} />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal de detalhes (reuso do CRM) */}
            <NegotiationDetailModal
                negotiationId={detailId}
                isOpen={detailId !== null}
                onClose={() => setDetailId(null)}
                onUpdate={fetchLeads}
            />
        </div>
    );
};

export default MapaLeadsView;
